<script>
	//@ts-check
	import ky, { HTTPError } from 'ky';
	import { getLocaleState, getTranslationsFunction } from '$lib/utils/i18n.js';
	import { getKyOptions } from '$lib/utils/api.js';
	import messages from '$lib/messages/login.js';

	const localeState = getLocaleState(messages);
	const t = getTranslationsFunction({ localeState });

	let email = $state('');
	let password = $state('');
	let isLoading = $state(false);
	let errorMessage = $state('');
	let stateParam = $state('');
	let timeRemaining = $state(300); // 5 minutes in seconds

	// Derive the main app (my2) origin from the current login host
	// (login.<basedomain> -> my2.<basedomain>). Environment-agnostic across
	// lab/dev/prod — avoids hardcoding a base domain or lab name. Read lazily so
	// window is only accessed in the browser (this route is client-only).
	const getMy2Domain = () => `https://${window.location.host.replace(/^login\./, 'my2.')}`;

	// Format time remaining as MM:SS
	const formattedTime = $derived(() => {
		const minutes = Math.floor(timeRemaining / 60);
		const seconds = timeRemaining % 60;
		return `${minutes}:${seconds.toString().padStart(2, '0')}`;
	});

	// Check if time is running low (less than 2 minutes)
	const isTimeLow = $derived(timeRemaining < 120);

	$effect(() => {
		// Extract state parameter from URL on mount
		const urlParams = new URLSearchParams(window.location.search);
		stateParam = urlParams.get('state') || '';

		if (!stateParam) {
			// User arrived without a state parameter - redirect to main app
			// The main app will initiate the proper login flow with a state token
			console.info('No state parameter found, redirecting to main application...');
			window.location.href = getMy2Domain();
			return;
		}

		// Start countdown timer
		const interval = setInterval(() => {
			timeRemaining--;

			if (timeRemaining <= 0) {
				clearInterval(interval);
				console.info('Session expired, redirecting to main application...');
				window.location.href = getMy2Domain();
			}
		}, 1000);

		// Cleanup interval on unmount
		return () => clearInterval(interval);
	});

	/**
	 * @param {Event & { currentTarget: EventTarget & HTMLFormElement }} event
	 */
	async function handleSubmit(event) {
		event.preventDefault();
		isLoading = true;
		errorMessage = '';

		if (!stateParam) {
			errorMessage = 'Invalid login request. Please try again.';
			isLoading = false;
			return;
		}

		try {
			console.log('Login attempt:', { email, stateParam });

			// Call the login API using ky
			const data = await ky
				.post('/api/login/login', {
					...getKyOptions(),
					json: {
						username: email,
						password: password,
						state: stateParam
					}
				})
				.json();

			if (data.ok && data.exchange_token && data.state) {
				// Successful login - redirect to callback
				// Use callbackHost from response (supports localhost dev), fallback to derived my2 origin
				const callbackHost = data.callbackHost || getMy2Domain();
				console.log('Login successful, redirecting to callback...', { callbackHost });
				const callbackUrl = `${callbackHost}/auth/callback/?exchange_token=${data.exchange_token}&state=${data.state}`;
				window.location.href = callbackUrl;
			} else {
				errorMessage = 'Invalid response from server. Please try again.';
			}
		} catch (error) {
			console.error('Login error:', error);

			// Handle HTTP errors from ky
			if (error instanceof HTTPError) {
				const response = error.response;
				try {
					const data = error.data;

					// Check if error is due to invalid/expired state
					if (response.status === 401 || response.status === 400) {
						const errorMsg = data.message || '';
						if (errorMsg.toLowerCase().includes('state') ||
						    errorMsg.toLowerCase().includes('expired') ||
						    errorMsg.toLowerCase().includes('invalid')) {
							console.warn('State may be expired or invalid, redirecting to main app...');
							errorMessage = 'Your session has expired. Redirecting...';
							// Give user a moment to see the message, then redirect
							setTimeout(() => {
								window.location.href = getMy2Domain();
							}, 2000);
							return;
						}
					}

					errorMessage = data.message || 'Authentication failed. Please check your credentials.';
				} catch {
					errorMessage = 'Authentication failed. Please check your credentials.';
				}
			} else {
				errorMessage = 'An error occurred. Please try again.';
			}
		} finally {
			isLoading = false;
		}
	}
</script>

<svelte:head>
	<title>Sign In - Spectralink AMIE Advanced</title>
</svelte:head>

<div class="min-h-screen flex items-center justify-center px-4 py-12">
	<div class="w-full max-w-md">
		<!-- Logo and Branding -->
		<div class="text-center mb-8">
			<div class="flex justify-center mb-4">
				<img src="/images/logo.png" alt="Spectralink" class="h-12" />
			</div>
			<h1 class="text-2xl font-bold text-(--color-text-primary) mb-2">AMIE Advanced</h1>
			<p class="text-(--color-text-secondary)">{t("sign_in_to_your_account")}</p>
		</div>

		<!-- Login Card -->
		<div class="bg-(--color-card-bg) rounded-lg shadow-lg border border-(--color-card-border) p-8">
			<!-- Session Timer -->
			{#if stateParam}
				<div class="mb-6 p-3 rounded-lg flex items-center justify-between {isTimeLow ? 'bg-(--color-warning-lighter) border border-(--color-warning-light)' : 'bg-(--color-info-lighter) border border-(--color-info-light)'}">
					<div class="flex items-center gap-2">
						<i class="fa-solid fa-clock {isTimeLow ? 'text-(--color-warning)' : 'text-(--color-info)'}"></i>
						<span class="text-sm text-(--color-text-primary)">
							{isTimeLow ? t('Session expiring soon') : t('Session time remaining')}
						</span>
					</div>
					<span class="font-mono font-bold {isTimeLow ? 'text-(--color-warning)' : 'text-(--color-info)'}">{formattedTime()}</span>
				</div>
			{/if}

			{#if errorMessage}
				<div class="mb-6 p-4 bg-(--color-error-lighter) border border-(--color-error-light) rounded-lg flex items-start gap-3">
					<i class="fa-solid fa-circle-exclamation text-(--color-error) mt-0.5"></i>
					<div class="flex-1">
						<p class="text-sm text-(--color-text-primary)">{errorMessage}</p>
					</div>
				</div>
			{/if}

			<form onsubmit={handleSubmit}>
				<!-- Email Field -->
				<div class="mb-4">
					<label for="email" class="block text-sm font-medium text-(--color-text-primary) mb-2">
						{t("email_address")}
					</label>
					<input
						type="email"
						id="email"
						bind:value={email}
						required
						autocomplete="email"
						disabled={isLoading}
						class="w-full px-4 py-2.5 bg-(--color-input-bg) border border-(--color-input-border) rounded-lg text-(--color-input-text) placeholder:text-(--color-input-placeholder) focus:outline-none focus:ring-2 focus:ring-(--color-input-border-focus) focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
						placeholder="you@example.com"
					/>
				</div>

				<!-- Password Field -->
				<div class="mb-4">
					<div class="flex items-center justify-between mb-2">
						<label for="password" class="block text-sm font-medium text-(--color-text-primary)">
							{t("password")}
						</label>
						<a href="/forgot-password" class="text-sm font-medium text-(--color-primary) hover:text-(--color-primary-hover) transition-colors">
							{t("forgot_password?")}
						</a>
					</div>
					<input
						type="password"
						id="password"
						bind:value={password}
						required
						autocomplete="current-password"
						disabled={isLoading}
						class="w-full px-4 py-2.5 bg-(--color-input-bg) border border-(--color-input-border) rounded-lg text-(--color-input-text) placeholder:text-(--color-input-placeholder) focus:outline-none focus:ring-2 focus:ring-(--color-input-border-focus) focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
						placeholder="Enter your password"
					/>
				</div>

				<!-- Sign In Button -->
				<button
					type="submit"
					disabled={isLoading}
					class="w-full py-2.5 px-4 bg-(--color-btn-primary-bg) hover:bg-(--color-btn-primary-hover) text-(--color-btn-primary-text) font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-(--color-primary) focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
				>
					{#if isLoading}
						<i class="fa-solid fa-spinner fa-spin"></i>
						<span>{t("signing_in")}...</span>
					{:else}
						<span>{t("sign_in")}</span>
					{/if}
				</button>
			</form>
		</div>

		<!-- Footer Links -->
		<div class="mt-6 text-center text-sm text-(--color-text-secondary)">
			<p>
				{t("dont_have_an_account?")}
				<a href="/signup" class="font-medium text-(--color-primary) hover:text-(--color-primary-hover) transition-colors">
					{t("Contact your administrator")}
				</a>
			</p>
		</div>

		<!-- Additional Footer -->
		<div class="mt-8 text-center text-xs text-(--color-text-tertiary)">
			<p>&copy; {t("copyrightstr")}</p>
			<div class="mt-2 flex items-center justify-center gap-4">
				<a href="/privacy" class="hover:text-(--color-text-secondary) transition-colors">{t("privacy_policy")}</a>
				<span>&bull;</span>
				<a href="/terms" class="hover:text-(--color-text-secondary) transition-colors">{t("terms_of_service")}</a>
			</div>
		</div>
	</div>
</div>
